import React, { useCallback, useEffect, useRef, useState } from 'react';
import './RecipeScaleModal.css';

const formatPercent = (scale) => {
  const percent = Number(scale) * 100;
  if (!Number.isFinite(percent) || percent <= 0) return '100';
  return String(Math.round(percent * 100) / 100);
};

const RecipeScaleModal = ({ close, currentScale = 1, onScale, modalService }) => {
  const [percent, setPercent] = useState(() => formatPercent(currentScale));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const closeGuardCleanupRef = useRef(null);
  const onBeforeClose = modalService?.onBeforeClose;
  const modals = modalService?.modals;

  const clearCloseGuard = useCallback(() => {
    closeGuardCleanupRef.current?.();
    closeGuardCleanupRef.current = null;
  }, []);

  useEffect(() => () => {
    closeGuardCleanupRef.current?.();
  }, []);

  useEffect(() => {
    if (!saved) return;
    const topModal = modals?.[modals.length - 1];
    if (!topModal || topModal.props?.onScale === onScale) {
      clearCloseGuard();
      close();
    }
  }, [saved, modals, onScale, close, clearCloseGuard]);

  const submit = async (event) => {
    event.preventDefault();
    const numericPercent = Number(percent);
    if (!Number.isFinite(numericPercent) || numericPercent <= 0) {
      setError('Enter a percentage greater than 0.');
      return;
    }

    setSaving(true);
    setError('');
    closeGuardCleanupRef.current = onBeforeClose?.(() => false) || null;
    try {
      await onScale(numericPercent / 100);
      setSaving(false);
      setSaved(true);
    } catch (submissionError) {
      clearCloseGuard();
      setError(submissionError?.message || 'Recipe amount could not be updated.');
      setSaving(false);
    }
  };

  return (
    <form className="recipe-scale-modal" data-invalid={Boolean(error)} aria-busy={saving} onSubmit={submit}>
      <h2>Scale recipe</h2>
      <label htmlFor="recipe-scale-percent">Recipe amount (%)</label>
      <div className="recipe-scale-input-wrap">
        <input
          id="recipe-scale-percent"
          type="number"
          inputMode="decimal"
          min="0.01"
          step="any"
          value={percent}
          onChange={(event) => {
            setPercent(event.target.value);
            if (error) setError('');
          }}
          autoFocus
          aria-invalid={Boolean(error)}
          aria-describedby={error ? 'recipe-scale-help recipe-scale-error' : 'recipe-scale-help'}
        />
        <span aria-hidden="true">%</span>
      </div>
      <p id="recipe-scale-help" className="recipe-scale-help">
        100% = full recipe · 50% = half recipe
      </p>
      {error && <p id="recipe-scale-error" className="recipe-scale-error" role="alert">{error}</p>}
      <div className="recipe-scale-actions">
        <button type="button" className="recipe-scale-cancel" onClick={close} disabled={saving}>Cancel</button>
        <button type="submit" className="recipe-scale-apply" disabled={saving}>
          {saving ? 'Updating…' : 'Apply'}
        </button>
      </div>
    </form>
  );
};

export default RecipeScaleModal;
